import React, { useState, useRef, useEffect } from 'react';
import { Share2, MessageCircle, Facebook, Twitter, Linkedin, Mail, Link as LinkIcon, Check } from 'lucide-react';

/**
 * One-click share button.
 *
 *  - Mobile (or any browser exposing `navigator.share`): tap → native OS share
 *    sheet. This is the best UX, especially in India where users mostly share
 *    via WhatsApp directly from the system sheet.
 *  - Desktop: tap → dropdown with WhatsApp, Facebook, X, LinkedIn, Email +
 *    "Copy link" fallback.
 *
 * Props
 *   title   – display text used in the share sheet
 *   text    – longer description sent along with the URL (WhatsApp/Email/Twitter)
 *   url     – absolute share URL (defaults to current location)
 *   compact – render icon-only pill (default false → icon + "Share" label)
 *   testid  – data-testid prefix for the trigger
 */
const ShareButton = ({ title, text, url, compact = false, testid = 'share' }) => {
  const shareUrl = (url || (typeof window !== 'undefined' ? window.location.href : '')).split('#')[0];
  const shareTitle = title || (typeof document !== 'undefined' ? document.title : '');
  const shareText = text || shareTitle;
  const composed = `${shareText}\n${shareUrl}`;

  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handlePrimaryClick = async () => {
    // Try the native Web Share API first — best on mobile / supported desktops.
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        return;
      } catch (e) {
        // User cancelled — fall through to dropdown only if not AbortError.
        if (e && e.name !== 'AbortError') console.warn('navigator.share failed', e);
        if (e && e.name === 'AbortError') return;
      }
    }
    setOpen((v) => !v);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // fallback — old browsers
      window.prompt('Copy link', shareUrl);
    }
  };

  const targets = [
    {
      key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'text-emerald-600 hover:bg-emerald-50',
      href: `https://wa.me/?text=${encodeURIComponent(composed)}`,
    },
    {
      key: 'facebook', label: 'Facebook', icon: Facebook, color: 'text-[#1877f2] hover:bg-blue-50',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    },
    {
      key: 'twitter', label: 'X / Twitter', icon: Twitter, color: 'text-gray-900 hover:bg-gray-100',
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    },
    {
      key: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: 'text-[#0a66c2] hover:bg-blue-50',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
    {
      key: 'email', label: 'Email', icon: Mail, color: 'text-gray-700 hover:bg-gray-100',
      href: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(composed)}`,
    },
  ];

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        onClick={handlePrimaryClick}
        className={`inline-flex items-center gap-1.5 text-xs font-semibold border rounded-full transition-colors ${
          compact ? 'p-1.5' : 'px-3 py-1.5'
        } bg-white text-[#2c54b2] border-[#2c54b2]/30 hover:bg-[#2c54b2] hover:text-white`}
        title="Share"
        data-testid={`${testid}-btn`}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Share2 className="w-3.5 h-3.5" />
        {!compact && <span>Share</span>}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-xl z-50 overflow-hidden"
          data-testid={`${testid}-menu`}
          role="menu"
        >
          {targets.map((t) => {
            const Icon = t.icon;
            return (
              <a
                key={t.key}
                href={t.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className={`flex items-center gap-2 px-3 py-2 text-sm ${t.color} transition-colors`}
                role="menuitem"
                data-testid={`${testid}-${t.key}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </a>
            );
          })}
          <button
            type="button"
            onClick={copy}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-t border-gray-100"
            data-testid={`${testid}-copy`}
          >
            {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <LinkIcon className="w-4 h-4" />}
            {copied ? 'Link copied!' : 'Copy link'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ShareButton;
